/**
 * Parsers/formatters for the unit-string value formats used in resource.json (`BatterySteering`).
 *
 * resource.json stores quantities as strings with units — ActivePower `"1.5 MW"`, PowerRampRate
 * `"2 kW/s"`, Period `"200 s"` — and state-of-charge as a Fraction string `"0.9"` (= 90%). The form
 * works in display units (kW, kW/s, s, %); these helpers convert in/out and bound precision so a
 * round-trip never introduces float noise like `0.30000000004`.
 */

const QUANTITY_PATTERN = /^\s*(-?\d+(?:\.\d+)?)\s*([a-zA-Z/]+)\s*$/

/** Round to a fixed number of decimals and drop trailing zeros (12.300 -> 12.3, 0.30000000004 -> 0.3). */
function roundTo(value: number, decimals: number): number {
  return Number.parseFloat(value.toFixed(decimals))
}

function parseQuantity(raw: string, unitFactors: Record<string, number>): number {
  const match = QUANTITY_PATTERN.exec(raw)
  if (!match) {
    throw new Error(`Unparseable quantity: "${raw}"`)
  }
  const [, numberPart, unit] = match
  const factor = unitFactors[unit]
  if (factor === undefined) {
    throw new Error(`Unknown unit "${unit}" in "${raw}" (expected one of ${Object.keys(unitFactors).join(', ')})`)
  }
  // Convert the parsed number into the base unit (e.g. MW -> kW), rounded to avoid float drift.
  return roundTo(Number.parseFloat(numberPart) * factor, 6)
}

// --- Fraction (state of charge): "0.9" <-> 90 (%) -------------------------------------------------

const FRACTION_PATTERN = /^\s*-?\d+(?:\.\d+)?\s*$/

export function parseFractionToPercent(raw: string): number {
  // Numeric-only guard. Without this `parseFloat('5kW')` would silently return 5 and we'd treat
  // the resource.json value as a fraction-of-1, producing 500%. Match the other unit parsers,
  // which already reject trailing-junk strings via QUANTITY_PATTERN.
  if (!FRACTION_PATTERN.test(raw)) {
    throw new Error(`Unparseable fraction: "${raw}"`)
  }
  const fraction = Number.parseFloat(raw)
  if (Number.isNaN(fraction)) {
    throw new Error(`Unparseable fraction: "${raw}"`)
  }
  return roundTo(fraction * 100, 4)
}

export function formatPercentToFraction(percent: number): string {
  // 90 -> "0.9", 5 -> "0.05"; bounded so we never emit "0.30000000004".
  return String(roundTo(percent / 100, 6))
}

// --- ActivePower: "1.5 MW" / "500 kW" / "0 W" <-> kW ---------------------------------------------

const POWER_TO_KW: Record<string, number> = { W: 0.001, kW: 1, MW: 1000, GW: 1_000_000 }

export function parseActivePowerToKw(raw: string): number {
  return parseQuantity(raw, POWER_TO_KW)
}

export function formatKwToActivePower(kw: number): string {
  return `${roundTo(kw, 3)} kW`
}

// --- PowerRampRate: "2 kW/s" / "0 W/s" <-> kW/s --------------------------------------------------

const RAMP_TO_KW_PER_S: Record<string, number> = { 'W/s': 0.001, 'kW/s': 1, 'MW/s': 1000 }

export function parsePowerRampRateToKwPerSecond(raw: string): number {
  return parseQuantity(raw, RAMP_TO_KW_PER_S)
}

export function formatKwPerSecondToPowerRampRate(kwPerSecond: number): string {
  return `${roundTo(kwPerSecond, 3)} kW/s`
}

// --- Period: "200 s" / "0.7 s" <-> seconds -------------------------------------------------------

const PERIOD_TO_SECONDS: Record<string, number> = { ms: 0.001, s: 1 }

export function parsePeriodToSeconds(raw: string): number {
  return parseQuantity(raw, PERIOD_TO_SECONDS)
}

export function formatSecondsToPeriod(seconds: number): string {
  return `${roundTo(seconds, 3)} s`
}
